import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { APColumn, APSizedBox, APText, getIt, showErrorDialog, useCountDown } from "ap-components";
import { RouteComponentProps } from "@gatsbyjs/reach-router";
import { launchPage } from "../../Common/Utils";
import SharedPrefs from "../../Services/SharedPrefs";
import MutualFundApiServices from "../../Services/MutualFundServices";

// Inline styles for circular progress components
const progressStyles = {
    root: {
        position: "relative" as const,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center"
    },
    bottom: {
        color: "#e0e7f6"
    },
    top: {
        color: "#142e5a",
        animationDuration: "550ms",
        position: "absolute" as const,
        left: 0,
        top: 0
    },
    circle: {
        strokeLinecap: "round" as const
    }
};

export default function LoadingTimer(props: RouteComponentProps<{ mandateId: string }>) {
    const totalTime = 1000 * 60 * 3
    const [time, { start }] = useCountDown(totalTime);
    const [newMandateId, setNewMandateId] = useState<string>();

    useEffect(() => {
        // Start timer immediately when component mounts
        start();
    }, [start]);

    useEffect(() => {
        // Restart timer when it reaches 0 (infinite loop behavior)
        if (time === 0) {
            start();
        }
    }, [time, start]);

    useEffect(() => {
        if (time % 5000 === 0 && newMandateId && time > 0) {
            getDigioLink();
        }
    }, [time, newMandateId])

    useEffect(() => {
        getMandateId();
    }, [])

    const mandateToken = getIt(SharedPrefs).getItem('mandateTkn');

    if (!mandateToken) {
        launchPage('/');
        showErrorDialog("Unauthorized Access");
        return <></>
    }

    if (!props.mandateId) {
        window.history.back();
        showErrorDialog("Please select correct mandate");
        return <></>
    }

    async function getMandateId() {
        if (!mandateToken || !props.mandateId) {
            return
        }

        let res = await getIt(MutualFundApiServices).registerMandateNewIfExpired(props.mandateId, mandateToken);
        setNewMandateId(res._id);
    }

    async function getDigioLink() {
        if (!mandateToken || !props.mandateId) {
            return
        }

        try {
            var res = await getIt(MutualFundApiServices).getDigioLink(props.mandateId, mandateToken);
            const a = document.createElement('a');
            a.href = res;
            a.rel = 'noreferrer noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (error) {
            // Since this will be failing a lot incase of the a fresh online mandate register, we need not show any error to the user;    
        }
    }


    var minutes = Math.floor(time / 60000);
    var seconds = Math.floor((time % 60000) / 1000);

    let progress = Math.min(100, Math.max(0, 100 - (100 * time / totalTime)));

    return (
        <APColumn mainAxisSize="max" crossAxisAlignment="center" mainAxisAlignment="center" style={{ padding: "30px" }} >
            <APText style={{ lineHeight: 1.5, color: "#41495b", fontSize: "16px", textAlign: "center" }}>
                Please wait as the process will take upto 3 minutes. Do not press back or refresh
            </APText>
            <APSizedBox height="40px" />
            <div style={progressStyles.root} >
                <APText
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        letterSpacing: 2,
                        fontWeight: 500,
                        color: "#070417",
                        fontSize: "40px",
                        transform: "translate(-50%, -50%)"
                    }}
                >
                    0{minutes}:{seconds.toString().padStart(2, '0')}
                </APText>
                <CircularProgress
                    variant="determinate"
                    sx={progressStyles.bottom}
                    size={220}
                    thickness={4}
                    value={100}
                />
                <CircularProgress
                    variant="determinate"
                    disableShrink
                    sx={{
                        ...progressStyles.top,
                        '& .MuiCircularProgress-circle': progressStyles.circle
                    }}
                    size={220}
                    thickness={4}
                    value={progress}
                />
            </div>
            <APSizedBox height="24px" />
            <span style={{ lineHeight: 1.5, color: "#41495b", fontSize: "16px" }}>
                You are being redirected...
            </span>
        </APColumn>
    );
}
